import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, OnDestroy, signal } from '@angular/core';
import { EInvoiceViewerSearchHandler } from '../../interfaces/einvoice-viewer-search-handler.interface';
import { EInvoiceViewerSearchOptions } from '../../interfaces/einvoice-viewer-search-options.interface';
import { FormatMatchResult } from '../../interfaces/format-match-result.interface';
import { EinvoiceViewerContextService } from '../../services/einvoice-viewer-context.service';
import { EinvoiceViewerSearchService } from '../../services/einvoice-viewer-search.service';
import { InvoiceService } from '../../services/invoice.service';
import { EInvoiceFieldSelection, EInvoiceProvenanceIndex } from '../../utils/provenance-index';
import { matchesSearchTexts, normalizeSearchText } from '../../utils/viewer-search.util';

interface EInvoiceModelTreeSearchTarget {
  id: string;
  texts: string[];
}

interface EInvoiceModelTreeNode {
  id: string;
  code?: string;
  label: string;
  tooltip?: string;
  context?: string;
  depth: number;
  entry?: EInvoiceFieldSelection;
  children: EInvoiceModelTreeNode[];
}

interface EInvoiceSchemaLabel {
  term: string;
  label: string;
  title: string;
  description?: string;
}

interface EInvoiceSchemaLabelIndex {
  byTerm: Map<string, EInvoiceSchemaLabel>;
  byModelPath: Map<string, EInvoiceSchemaLabel>;
}

const EMPTY_SCHEMA_LABEL_INDEX: EInvoiceSchemaLabelIndex = {
  byTerm: new Map<string, EInvoiceSchemaLabel>(),
  byModelPath: new Map<string, EInvoiceSchemaLabel>()
};

@Component({
  selector: 'app-einvoice-model-tree',
  templateUrl: './model-tree.component.html',
  styleUrls: ['./model-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EinvoiceModelTreeComponent implements EInvoiceViewerSearchHandler, OnDestroy {
  private readonly einvoiceViewerContextService = inject(EinvoiceViewerContextService);
  private readonly einvoiceViewerSearchService = inject(EinvoiceViewerSearchService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly invoiceService = inject(InvoiceService);
  private lastScrolledModelPath: string | undefined;
  private schemaLoadSequence = 0;

  private readonly schemaLabels = signal<EInvoiceSchemaLabelIndex>(EMPTY_SCHEMA_LABEL_INDEX);
  readonly searchOptions = signal<EInvoiceViewerSearchOptions>({ query: '', exactMatch: false });
  readonly activeSearchResultIndex = signal(0);
  private readonly copiedModelPath = signal<string | undefined>(undefined);
  private copiedResetHandle: ReturnType<typeof setTimeout> | undefined;

  readonly selectedModelPaths = this.einvoiceViewerContextService.selectedModelPaths;
  readonly activeModelPath = this.einvoiceViewerContextService.activeModelPath;
  readonly copiedModelPathValue = this.copiedModelPath.asReadonly();
  readonly nodes = computed(() => buildModelTreeNodes(
    this.einvoiceViewerContextService.provenanceIndex(),
    this.schemaLabels()
  ));
  readonly searchTargets = computed(() => flattenSearchTargets(this.nodes()));
  readonly searchResults = computed(() => {
    const searchOptions = this.searchOptions();
    if (!normalizeSearchText(searchOptions.query)) {
      return [];
    }

    return this.searchTargets()
      .filter(target => matchesSearchTexts(target.texts, searchOptions))
      .map(target => target.id);
  });
  readonly searchResultIds = computed(() => new Set(this.searchResults()));
  readonly matchCount = computed(() => this.searchResults().length);
  readonly activeMatchIndex = computed(() => this.searchResults().length === 0 ? -1 : this.activeSearchResultIndex());
  readonly currentSearchResultId = computed(() => this.searchResults()[this.activeMatchIndex()]);

  constructor() {
    this.einvoiceViewerSearchService.registerHandler('modelTree', this);

    effect(() => {
      void this.loadSchemaLabels(this.einvoiceViewerContextService.format());
    });

    effect(() => {
      const activeModelPath = this.einvoiceViewerContextService.activeModelPath();
      if (!activeModelPath || this.lastScrolledModelPath === activeModelPath) {
        return;
      }

      this.lastScrolledModelPath = activeModelPath;
      requestAnimationFrame(() => scrollElementWithAttribute(this.elementRef.nativeElement, 'data-model-path', activeModelPath));
    });
  }

  ngOnDestroy(): void {
    this.einvoiceViewerSearchService.unregisterHandler('modelTree', this);
  }

  setSearchQuery(options: EInvoiceViewerSearchOptions): void {
    this.searchOptions.set(options);
    this.activeSearchResultIndex.set(0);
  }

  nextSearchResult(): void {
    this.moveSearchResult(1);
  }

  previousSearchResult(): void {
    this.moveSearchResult(-1);
  }

  scrollToActiveResult(): void {
    const currentSearchResultId = this.currentSearchResultId();
    if (!currentSearchResultId) {
      return;
    }

    requestAnimationFrame(() => scrollElementWithAttribute(this.elementRef.nativeElement, 'data-search-id', currentSearchResultId));
  }

  select(entry: EInvoiceFieldSelection | undefined, event: MouseEvent): void {
    if (!entry) {
      return;
    }

    event.stopPropagation();
    this.einvoiceViewerContextService.select(entry, event.ctrlKey || event.metaKey);
  }

  copyValue(entry: EInvoiceFieldSelection | undefined, event: MouseEvent): void {
    event.stopPropagation();
    const value = entry?.valuePreview;
    if (!value) {
      return;
    }

    void navigator.clipboard?.writeText(value);
    this.copiedModelPath.set(entry?.modelPath);
    clearTimeout(this.copiedResetHandle);
    this.copiedResetHandle = setTimeout(() => this.copiedModelPath.set(undefined), 1100);
  }

  private moveSearchResult(direction: 1 | -1): void {
    const searchResults = this.searchResults();
    if (searchResults.length === 0) {
      return;
    }

    const nextIndex = (this.activeSearchResultIndex() + direction + searchResults.length) % searchResults.length;
    this.activeSearchResultIndex.set(nextIndex);
    this.scrollToActiveResult();
  }

  private async loadSchemaLabels(format: FormatMatchResult | undefined): Promise<void> {
    const loadSequence = ++this.schemaLoadSequence;
    if (!format) {
      this.schemaLabels.set(EMPTY_SCHEMA_LABEL_INDEX);
      return;
    }

    try {
      const schema = await this.invoiceService.getModelSchema(format);
      if (loadSequence !== this.schemaLoadSequence) {
        return;
      }

      this.schemaLabels.set(schema ? buildSchemaLabelIndex(schema) : EMPTY_SCHEMA_LABEL_INDEX);
    } catch (error) {
      if (loadSequence !== this.schemaLoadSequence) {
        return;
      }

      console.warn('Could not load e-invoice model schema labels.', error);
      this.schemaLabels.set(EMPTY_SCHEMA_LABEL_INDEX);
    }
  }
}

function buildModelTreeNodes(index: EInvoiceProvenanceIndex, schemaLabels: EInvoiceSchemaLabelIndex): EInvoiceModelTreeNode[] {
  const roots: EInvoiceModelTreeNode[] = [];
  const groups = new Map<string, EInvoiceModelTreeNode>();

  for (const modelPath of index.orderedModelPaths) {
    const entry = index.byModelPath.get(modelPath);
    if (!entry) {
      continue;
    }

    let children = roots;
    let groupId = '';
    const groupSegments = getBusinessGroupSegments(entry.termPath);
    const groupModelPaths = getGroupModelPaths(entry.modelPath, groupSegments.length);
    const instancePrefixes = getGroupInstancePrefixes(entry.modelPath);
    const instancePrefixByDepth = getInstancePrefixByDepth(groupSegments.length, instancePrefixes);
    for (let depth = 0; depth < groupSegments.length; depth++) {
      const segment = groupSegments[depth];
      const groupModelPath = groupModelPaths[depth];
      const instancePrefix = instancePrefixByDepth[depth];
      groupId = `${groupId}/${segment}${instancePrefix ? `:${instancePrefix}` : ''}`;
      let group = groups.get(groupId);
      if (!group) {
        const schemaLabel = groupModelPath
          ? schemaLabels.byModelPath.get(groupModelPath) ?? schemaLabels.byTerm.get(segment)
          : schemaLabels.byTerm.get(segment);
        const fallbackLabel = humanizePathSegment(getLastPathSegment(groupModelPath)) ?? segment;
        group = {
          id: groupId,
          code: segment,
          label: schemaLabel?.label ?? fallbackLabel,
          tooltip: schemaLabel?.description ?? schemaLabel?.title,
          context: instancePrefix ? formatInstanceContext(instancePrefix) : undefined,
          depth,
          children: []
        };
        groups.set(groupId, group);
        children.push(group);
      }
      children = group.children;
    }

    const normalizedModelPath = normalizeModelPath(entry.modelPath);
    const schemaLabel = schemaLabels.byModelPath.get(normalizedModelPath)
      ?? (entry.term ? schemaLabels.byTerm.get(entry.term) : undefined);
    children.push({
      id: `field:${entry.modelPath}`,
      code: entry.term,
      label: schemaLabel?.label ?? humanizePathSegment(getLastPathSegment(normalizedModelPath)) ?? entry.term ?? entry.modelPath,
      tooltip: schemaLabel?.description ?? schemaLabel?.title ?? entry.modelPath,
      depth: groupSegments.length,
      entry,
      children: []
    });
  }

  return roots;
}

function flattenSearchTargets(nodes: EInvoiceModelTreeNode[]): EInvoiceModelTreeSearchTarget[] {
  const targets: EInvoiceModelTreeSearchTarget[] = [];

  for (const node of nodes) {
    const searchParts = [
      node.code,
      node.label,
      node.context,
      node.entry?.term,
      node.entry?.termPath,
      node.entry?.valuePreview
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    targets.push({
      id: node.id,
      texts: searchParts
    });

    if (node.children.length > 0) {
      targets.push(...flattenSearchTargets(node.children));
    }
  }

  return targets;
}

function buildSchemaLabelIndex(schema: unknown): EInvoiceSchemaLabelIndex {
  const byTerm = new Map<string, EInvoiceSchemaLabel>();
  const byModelPath = new Map<string, EInvoiceSchemaLabel>();
  const root = asRecord(schema) ?? {};
  const definitions = asRecord(root['definitions']) ?? {};

  const register = (node: Record<string, unknown>, path: string[]): void => {
    const schemaLabel = parseSchemaTitle(node['title'], node['description']);
    if (!schemaLabel) {
      return;
    }
    if (!byTerm.has(schemaLabel.term)) {
      byTerm.set(schemaLabel.term, schemaLabel);
    }
    const modelPath = path.length > 0 ? `/${path.join('/')}` : undefined;
    if (modelPath && !byModelPath.has(modelPath)) {
      byModelPath.set(modelPath, schemaLabel);
    }
  };

  const descend = (node: Record<string, unknown> | undefined, path: string[], seenRefs: ReadonlySet<string>): void => {
    if (!node) {
      return;
    }

    const ref = typeof node['$ref'] === 'string' ? node['$ref'] : undefined;
    if (ref) {
      if (seenRefs.has(ref)) {
        return;
      }
      seenRefs = new Set(seenRefs).add(ref);
    }

    const resolved = resolveSchemaRef(node, definitions);
    if (resolved['type'] === 'array' || resolved['items'] !== undefined) {
      descend(asRecord(resolved['items']), path, seenRefs);
      return;
    }

    const properties = asRecord(resolved['properties']);
    if (!properties) {
      return;
    }

    for (const [key, child] of Object.entries(properties)) {
      const childNode = asRecord(child);
      if (!childNode) {
        continue;
      }
      const childPath = [...path, key.trim()];
      register(childNode, childPath);
      descend(childNode, childPath, seenRefs);
    }
  };

  descend(root, [], new Set<string>());
  return { byTerm, byModelPath };
}

function resolveSchemaRef(node: Record<string, unknown>, definitions: Record<string, unknown>): Record<string, unknown> {
  const ref = node['$ref'];
  if (typeof ref === 'string' && ref.startsWith('#/definitions/')) {
    const definition = asRecord(definitions[ref.slice('#/definitions/'.length)]) ?? {};
    return { ...definition, ...node };
  }

  return node;
}

function parseSchemaTitle(title: unknown, description?: unknown): EInvoiceSchemaLabel | undefined {
  if (typeof title !== 'string') {
    return undefined;
  }

  const match = /^\s*\[(B[GT]-\d+)\]\s*(.+?)\s*$/.exec(title);
  if (!match) {
    return undefined;
  }

  const trimmedDescription = typeof description === 'string' ? description.trim() : '';
  return {
    term: match[1],
    label: normalizeSchemaLabel(match[2]),
    title,
    description: trimmedDescription.length > 0 ? trimmedDescription : undefined
  };
}

function normalizeSchemaLabel(label: string): string {
  const normalized = label
    .replace(/\s+/g, ' ')
    .replace(/\.$/, '')
    .replace(/^A group of business terms providing information (about|on) /i, '')
    .replace(/^the /i, '')
    .trim();

  return normalized.length > 0
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : label;
}

function getBusinessGroupSegments(termPath: string | undefined): string[] {
  return termPath?.split('/').filter(segment => segment.startsWith('BG-')) ?? [];
}

function getGroupModelPaths(modelPath: string, groupCount: number): string[] {
  const propertySegments = modelPath.split('/').filter(part => part.length > 0 && !isArrayIndex(part));
  const groupSegmentCount = Math.max(0, propertySegments.length - 1);
  const result: string[] = [];
  for (let index = 0; index < Math.min(groupCount, groupSegmentCount); index++) {
    result.push(`/${propertySegments.slice(0, index + 1).join('/')}`);
  }

  return result;
}

function getGroupInstancePrefixes(modelPath: string): string[] {
  const parts = modelPath.split('/').filter(part => part.length > 0);
  const prefixes: string[] = [];
  for (let index = 0; index < parts.length; index++) {
    if (isArrayIndex(parts[index]) && hasNestedPropertyAfter(parts, index)) {
      prefixes.push(`/${parts.slice(0, index + 1).join('/')}`);
    }
  }

  return prefixes;
}

function getInstancePrefixByDepth(groupCount: number, instancePrefixes: string[]): Array<string | undefined> {
  const result = new Array<string | undefined>(groupCount).fill(undefined);
  if (groupCount === 0 || instancePrefixes.length === 0) {
    return result;
  }

  result[0] = instancePrefixes[0];
  const nestedPrefixes = instancePrefixes.slice(1);
  const firstNestedDepth = Math.max(1, groupCount - nestedPrefixes.length);
  for (let index = 0; index < nestedPrefixes.length; index++) {
    result[firstNestedDepth + index] = nestedPrefixes[index];
  }

  return result;
}

function formatInstanceContext(instancePrefix: string): string {
  const parts = instancePrefix.split('/').filter(part => part.length > 0);
  const index = Number(parts[parts.length - 1]) + 1;
  return `#${index}`;
}

function hasNestedPropertyAfter(parts: string[], index: number): boolean {
  return parts.slice(index + 1).some(part => !isArrayIndex(part));
}

function isArrayIndex(value: string): boolean {
  return /^\d+$/.test(value);
}

function normalizeModelPath(modelPath: string): string {
  const segments = modelPath.split('/').filter(part => part.length > 0 && !isArrayIndex(part));
  return `/${segments.join('/')}`;
}

function getLastPathSegment(path: string | undefined): string | undefined {
  return path?.split('/').filter(part => part.length > 0).at(-1);
}

function humanizePathSegment(segment: string | undefined): string | undefined {
  if (!segment) {
    return undefined;
  }

  return segment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function scrollElementWithAttribute(root: HTMLElement, attributeName: string, attributeValue: string): void {
  const element = Array.from(root.querySelectorAll<HTMLElement>(`[${attributeName}]`))
    .find(item => item.getAttribute(attributeName) === attributeValue);

  let parent = element?.parentElement;
  while (parent) {
    if (parent instanceof HTMLDetailsElement) {
      parent.open = true;
    }
    parent = parent.parentElement;
  }

  element?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
}